import { Add } from '@mui/icons-material';
import { Button, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import Chip from '/@/components/bayer/chip';
import { useCropProtectionProducts } from '/@/hooks/catalog/use-crop-protection-products';
import { useFieldById } from '/@/hooks/fields';
import { _ } from '/@/locale';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { ApplicationRegister } from '/@/types/application-register';
import type { ApplicationReport, ApplicationReportResult } from '/@/types/application-report';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { Recommendation } from '/@/types/recommendation';
import { reverse } from '/@/utils/router';
import { seedRecommendationUrl } from '/@/views/recommendations/seed-recommendation-route';
import { useCampaignRegistries } from '../use-campaign-registries';
import type { RegistryAddStep } from './components/add/types';
import RegistryApplication from './components/application';
import RegistryApplicationReport from './components/application-report';
import RegistryBarbecho from './components/barbecho';
import RegistryHarvest from './components/harvest';
import RegistryHarvestReminder from './components/harvest-reminder';
import RegistryNextCrop from './components/next-crop';
import Note from './components/note';
import RegistryPlanting from './components/planting';
import RegistryRecommendation from './components/recommendation';
import { buildRecommendedNameSet } from './components/recommendation-helpers';
import Timeline, { getCropState, type TimelineEntry } from './components/timeline';
import RegistryVisit from './components/visit';
import RegistryYieldEstimation from './components/yield-estimation';
import { hasYieldEstimation, yieldEstimationDate } from './components/yield-estimation-helpers';

type RegistryType = 'planting' | 'application' | 'recommendation' | 'visit' | 'note' | 'harvest' | 'yieldEstimation';

const CATEGORIES: { type: RegistryType; label: string }[] = [
    { type: 'planting', label: _('Siembra') },
    { type: 'application', label: _('Aplicaciones') },
    { type: 'recommendation', label: _('Recomendaciones') },
    { type: 'visit', label: _('Visitas') },
    { type: 'note', label: _('Notas') },
    { type: 'yieldEstimation', label: _('Estimación de rendimiento') },
    { type: 'harvest', label: _('Cosecha') },
];

/**
 * The harvest (and the reminder standing in for it) owns the day it falls on: it sorts last
 * among that day's entries, so after the timeline reverses to newest-first it heads its
 * state group and carries the "C" bubble. Without this a note logged later that afternoon
 * wins on raw timestamp — `harvestDate` is date-only, so it lands at midnight.
 */
const HARVEST_PRIORITY = 1;

type ApplicationGroup = [ApplicationRegister, ...ApplicationRegister[]];

/**
 * Group a field's application registers into cards. Cards are split by product type, then
 * by date: by default one card per application_date; for corn, one card per
 * crop-monitoring state — so each (type, state) shows at most one application card (with
 * multiple product rows). Applications of different types never share a card.
 */
const groupApplications = (registers: ApplicationRegister[], cropMonitoring?: CropMonitoring): ApplicationGroup[] => {
    const byState = cropMonitoring?.crop === 'corn';
    const groups = new Map<string, ApplicationGroup>();
    for (const register of registers) {
        const dateKey = byState
            ? (getCropState(register.applicationDate, cropMonitoring)?.code ?? register.applicationDate)
            : register.applicationDate;
        const key = `${register.type ?? 'none'}|${dateKey}`;
        const group = groups.get(key);
        if (group) group.push(register);
        else groups.set(key, [register]);
    }
    return [...groups.values()];
};

/** Earliest application_date in a group — places the card chronologically on the timeline. */
const earliestDate = (group: ApplicationGroup): string =>
    group.reduce(
        (min, register) => (register.applicationDate < min ? register.applicationDate : min),
        group[0].applicationDate
    );

type ApplicationReportResultGroup = [ApplicationReportResult, ...ApplicationReportResult[]];
/** A report narrowed to the results of a single applicationName — never empty. */
type ApplicationReportGroup = Omit<ApplicationReport, 'results'> & { results: ApplicationReportResultGroup };

/**
 * Split a FieldView application report into one card per applicationName. A report can
 * cover several distinct applications, and the card titles itself from the name of its
 * first result — so an ungrouped report shows every product it contains under whichever
 * application happened to come first. Insertion order is preserved, so the caller's
 * chronological placement still holds.
 */
const groupReportByApplicationName = (report: ApplicationReport): ApplicationReportGroup[] => {
    const groups = new Map<string, ApplicationReportResultGroup>();
    for (const result of report.results) {
        const group = groups.get(result.applicationName);
        if (group) group.push(result);
        else groups.set(result.applicationName, [result]);
    }
    return [...groups.values()].map((results) => ({ ...report, results }));
};

type RecommendationGroup = [Recommendation, ...Recommendation[]];

/**
 * Group a field's recommendations into a single "Productos recomendados" card — every pending
 * recommendation for the field belongs together regardless of type or target date.
 */
const groupRecommendations = (recommendations: Recommendation[]): RecommendationGroup[] => {
    if (recommendations.length === 0) return [];
    // Newest first.
    const sorted = [...recommendations].sort((a, b) => (a.created < b.created ? 1 : a.created > b.created ? -1 : 0));
    return [sorted as RecommendationGroup];
};

/** Earliest relevant date in a group — places the card chronologically on the timeline. */
const earliestRecommendationDate = (group: RecommendationGroup): string =>
    group.reduce((min, recommendation) => {
        const date = recommendation.created;
        return date < min ? date : min;
    }, group[0].created);

const Registries = ({
    fieldId,
    cropMonitoring,
    onOpenAddModal,
    onOpenYieldEstimation,
}: {
    fieldId?: number;
    cropMonitoring?: CropMonitoring;
    /** Opens the add-registry modal (owned by the parent) at the given step. */
    onOpenAddModal: (initialStep?: RegistryAddStep) => void;
    /** Opens the yield-estimation drawer (owned by the parent) on the given step / estimation. */
    onOpenYieldEstimation: (step?: 'form' | 'result', estimationId?: number) => void;
}) => {
    // No chip selected → show every registry type; a selected chip filters to that type only.
    const [selectedType, setSelectedType] = useState<RegistryType | null>(null);
    const navigate = useNavigate();
    const fieldQuery = useFieldById({ id: fieldId ?? 0 }, { enabled: !!fieldId });
    const campaign = cropMonitoring?.campaign || fieldQuery.data?.campaign;

    const handleRecommendHerbicide = useCallback(() => {
        mpTrackEvent('Crop Protection Recommendation began', {
            entry_source: 'field-detail-fallow',
            crop: 'corn',
            cp_type: 'weed',
        });
        navigate(
            reverse('cropProtectionRecommendation', {
                qs: {
                    fieldId,
                    crop: 'corn',
                    type: 'weeds',
                    userNodeId: fieldQuery.data?.userNode,
                },
            })
        );
    }, [navigate, fieldId, fieldQuery.data?.userNode]);

    const handleRecommendHybrid = useCallback(() => {
        mpTrackEvent('Generate Recommendation flow:Started', { entry_source: 'field-monitoring' });
        navigate(
            seedRecommendationUrl({
                crop: 'corn',
                fieldId,
                entrySource: 'field-monitoring',
                userNodeId: fieldQuery.data?.userNode,
            })
        );
    }, [navigate, fieldId, fieldQuery.data?.userNode]);

    const showNotes = selectedType === null || selectedType === 'note';
    const showApplications = selectedType === null || selectedType === 'application';
    const showPlanting = selectedType === null || selectedType === 'planting';
    const showRecommendation = selectedType === null || selectedType === 'recommendation';
    const showVisits = selectedType === null || selectedType === 'visit';
    const showHarvest = selectedType === null || selectedType === 'harvest';
    const showYieldEstimation = selectedType === null || selectedType === 'yieldEstimation';

    const {
        window: { dateGte, dateLte, isLoading: windowLoading },
        fetchEnabled,
        dateFetchEnabled,
        notesQuery,
        applicationsQuery,
        applicationReportsQuery,
        seedRecommendationsQuery,
        cropProtectionRecommendationsQuery,
        visitReportsQuery,
    } = useCampaignRegistries({ fieldId, cropMonitoring, enabled: Boolean(fieldId) });

    const { typeNameById } = useCropProtectionProducts();

    const notes = notesQuery.data?.results ?? [];
    const applications = applicationsQuery.data?.results ?? [];
    const applicationReports = (applicationReportsQuery.data?.results ?? []) as ApplicationReport[];
    const seedRecommendations = seedRecommendationsQuery.data?.results?.data ?? [];
    const cropProtectionRecommendations = cropProtectionRecommendationsQuery.data?.results?.data ?? [];
    const visitReports = visitReportsQuery.data?.results ?? [];

    const notesLoading = fetchEnabled && notesQuery.isLoading;
    const applicationsLoading = fetchEnabled && applicationsQuery.isLoading;
    const applicationReportsLoading = fetchEnabled && applicationReportsQuery.isLoading;
    const seedRecommendationsLoading = dateFetchEnabled && seedRecommendationsQuery.isLoading;
    const cropProtectionRecommendationsLoading = dateFetchEnabled && cropProtectionRecommendationsQuery.isLoading;
    const visitReportsLoading = fetchEnabled && visitReportsQuery.isLoading;

    const seedRecommendedNames = useMemo(() => buildRecommendedNameSet(seedRecommendations), [seedRecommendations]);
    const cropProtectionRecommendedNames = useMemo(
        () => buildRecommendedNameSet(cropProtectionRecommendations),
        [cropProtectionRecommendations]
    );

    const entries = useMemo<TimelineEntry[]>(() => {
        const allNotes = notes;

        // Planting event — one entry per crop monitoring, pinned to plantingDate.
        const plantingEntries: TimelineEntry[] =
            showPlanting && cropMonitoring?.plantingDate
                ? [
                      {
                          id: `planting-${cropMonitoring.id}`,
                          date: cropMonitoring.plantingDate,
                          children: (
                              <RegistryPlanting
                                  cropMonitoring={cropMonitoring}
                                  notes={allNotes.filter((n) => n.plantingDateCropMonitoring === cropMonitoring.id)}
                                  recommendedProductNames={seedRecommendedNames}
                              />
                          ),
                      },
                  ]
                : [];

        const noteEntries: TimelineEntry[] = showNotes
            ? allNotes
                  // Standalone campaign notes only — notes attached to a planting/application/
                  // report/harvest surface inside that register's card, not as their own
                  // timeline entry (otherwise they'd render — and be editable — twice).
                  .filter(
                      (note) =>
                          !note.plantingDateCropMonitoring &&
                          !note.applicationRegister &&
                          !note.applicationReportResult &&
                          !note.cornYieldEstimation &&
                          !note.closedCropMonitoring
                  )
                  // The notes query is not date-bounded (attached notes must survive the end of
                  // their campaign), so the campaign window is applied here instead — a
                  // standalone note is placed on the timeline by when it was written, and one
                  // written during a later campaign belongs to that campaign, not this one.
                  .filter((note) => {
                      const date = dayjs(note.created);
                      return (
                          (!dateGte || !date.isBefore(dayjs(dateGte), 'day')) && !date.isAfter(dayjs(dateLte), 'day')
                      );
                  })
                  .map((note) => ({
                      id: `note-${note.id}`,
                      date: note.created,
                      children: <Note note={note} />,
                  }))
            : [];

        const applicationEntries: TimelineEntry[] = showApplications
            ? groupApplications(applications, cropMonitoring).map((group) => {
                  const registerIds = new Set(group.map((register) => register.id));
                  const groupNotes = allNotes.filter(
                      (note) => note.applicationRegister && registerIds.has(note.applicationRegister)
                  );
                  return {
                      id: `application-${group[0].id}`,
                      date: earliestDate(group),
                      children: (
                          <RegistryApplication
                              registers={group}
                              // Fertilizantes registers have no crop-protection type — they're
                              // keyed by nitrogenSource instead (see add/application.tsx).
                              typeName={typeNameById(group[0].type) || _('Fertilizante')}
                              notes={groupNotes}
                              recommendedProductNames={cropProtectionRecommendedNames}
                          />
                      ),
                  };
              })
            : [];

        // FieldView application reports: one card per (report, applicationName), results
        // bounded to the campaign window; each card is placed at its earliest in-window
        // event date.
        const reportEntries: TimelineEntry[] = showApplications
            ? applicationReports
                  .map((report) => {
                      const windowResults = report.results.filter((result) => {
                          const date = dayjs(result.eventStartDate);
                          return (
                              (!dateGte || !date.isBefore(dayjs(dateGte), 'day')) &&
                              !date.isAfter(dayjs(dateLte), 'day')
                          );
                      });
                      return { ...report, results: windowResults };
                  })
                  .filter((report) => report.results.length > 0)
                  .flatMap((report) => groupReportByApplicationName(report))
                  .map((report) => {
                      const resultIds = new Set(report.results.map((result) => result.id));
                      const reportNotes = allNotes.filter(
                          (note) => note.applicationReportResult && resultIds.has(note.applicationReportResult)
                      );
                      // Dates are ISO (YYYY-MM-DD), so a lexical sort is chronological.
                      // results is non-empty here; dateLte is an unreachable fallback for the type.
                      const earliest = report.results.map((result) => result.eventStartDate).sort()[0] ?? dateLte;
                      return {
                          // A report id is no longer unique across cards — the first result's
                          // id identifies the applicationName group within it.
                          id: `application-report-${report.id}-${report.results[0].id}`,
                          date: earliest,
                          children: <RegistryApplicationReport report={report} notes={reportNotes} />,
                      };
                  })
            : [];

        // Both server-filtered result sets (seed/density by planting date, crop-protection by
        // created date) are merged and deduped by id, then grouped into a single "Productos
        // recomendados" card.
        const mergedRecommendations = showRecommendation
            ? [...seedRecommendations, ...cropProtectionRecommendations].filter(
                  (recommendation, index, all) => all.findIndex((other) => other.id === recommendation.id) === index
              )
            : [];
        const recommendationEntries: TimelineEntry[] = groupRecommendations(mergedRecommendations).map((group) => ({
            id: `recommendation-${group[0].id}`,
            date: earliestRecommendationDate(group),
            children: (
                <RegistryRecommendation
                    recommendations={group}
                    fieldId={fieldId}
                    cropMonitoring={cropMonitoring}
                    applications={applications}
                    applicationReports={applicationReports}
                />
            ),
        }));

        // One card per visit report that includes an observation for this field — reports
        // covering other fields (a single visit can span multiple lotes) are filtered out.
        const visitEntries: TimelineEntry[] = showVisits
            ? visitReports
                  .map((report) => ({
                      report,
                      fieldObservation: report.fieldObservations?.find((observation) => observation.field === fieldId),
                  }))
                  .filter(
                      (
                          entry
                      ): entry is typeof entry & { fieldObservation: NonNullable<typeof entry.fieldObservation> } =>
                          !!entry.fieldObservation
                  )
                  .map(({ report, fieldObservation }) => ({
                      id: `visit-${report.id}`,
                      date: report.date,
                      children: <RegistryVisit visitReport={report} fieldObservation={fieldObservation} />,
                  }))
            : [];

        // Harvest event — one entry per crop monitoring, pinned to harvestDate, once the crop
        // monitoring has a harvest date. This applies to every crop: only corn has a phenology
        // model, so non-corn harvests are recognised solely by their harvestDate.
        const harvestEntries: TimelineEntry[] =
            showHarvest && cropMonitoring?.harvestDate
                ? [
                      {
                          id: `harvest-${cropMonitoring.id}`,
                          date: cropMonitoring.harvestDate,
                          priority: HARVEST_PRIORITY,
                          children: (
                              <RegistryHarvest
                                  cropMonitoring={cropMonitoring}
                                  notes={allNotes.filter((n) => n.closedCropMonitoring === cropMonitoring.id)}
                              />
                          ),
                      },
                  ]
                : [];

        // Once 31+ days have passed since the corn's estimated harvest date and the harvest
        // still hasn't been registered, prompt for it — a dateless "Cosecha" step, same
        // pattern as the Barbecho/Siembra prompts.
        const daysSinceExpectedHarvest = cropMonitoring?.cornCropMonitoring?.expectedHarvestDate
            ? dayjs().diff(dayjs(cropMonitoring.cornCropMonitoring.expectedHarvestDate), 'day')
            : null;
        const harvestReminderEntries: TimelineEntry[] =
            showHarvest && cropMonitoring && !cropMonitoring.harvestDate && (daysSinceExpectedHarvest ?? 0) >= 31
                ? [
                      {
                          id: 'harvest-reminder',
                          date: dayjs().format('YYYY-MM-DD'),
                          state: { code: 'C' },
                          priority: HARVEST_PRIORITY,
                          children: <RegistryHarvestReminder onRegisterHarvest={() => onOpenAddModal('harvest')} />,
                      },
                  ]
                : [];

        // Yield estimation — corn-only. A campaign can have several; the card shows one row per
        // estimation. Notes are collected across ALL of the campaign's estimations (like grouped
        // applications) so they stay visible when a newer estimation is added; new notes attach to
        // the newest estimation (the card's representative).
        const estimationIds = new Set(
            (cropMonitoring?.cornCropMonitoring?.yieldEstimations ?? []).map((estimation) => estimation.id)
        );
        const yieldEstimationEntries: TimelineEntry[] =
            showYieldEstimation && hasYieldEstimation(cropMonitoring) && cropMonitoring
                ? [
                      {
                          id: `yield-estimation-${cropMonitoring.id}`,
                          date: yieldEstimationDate(cropMonitoring) ?? dayjs().format('YYYY-MM-DD'),
                          children: (
                              <RegistryYieldEstimation
                                  cropMonitoring={cropMonitoring}
                                  notes={allNotes.filter(
                                      (n) => n.cornYieldEstimation != null && estimationIds.has(n.cornYieldEstimation)
                                  )}
                                  onViewResults={(id) => onOpenYieldEstimation('result', id)}
                                  onAddEstimation={() => onOpenYieldEstimation('form')}
                              />
                          ),
                      },
                  ]
                : [];

        const otherEntries = [
            ...plantingEntries,
            ...noteEntries,
            ...applicationEntries,
            ...reportEntries,
            ...recommendationEntries,
            ...visitEntries,
            ...yieldEstimationEntries,
            ...harvestEntries,
            ...harvestReminderEntries,
        ];

        // No active crop monitoring → the field is in fallow ("Barbecho"). Show the
        // chemical-fallow-management CTA until a planting or an application is registered.
        // Once herbicide recommendations exist for the fallow window, its own "Productos
        // recomendados" card (with "Registrar lo aplicado") takes the CTA's place instead —
        // notes/visits logged in the meantime don't replace it, they just sit alongside it.
        const hasPlantingOrApplication =
            plantingEntries.length > 0 || applicationEntries.length > 0 || reportEntries.length > 0;
        const hasRecommendedProducts = recommendationEntries.length > 0;
        const barbechoEntries: TimelineEntry[] =
            cropMonitoring || hasPlantingOrApplication || hasRecommendedProducts
                ? []
                : [
                      {
                          id: 'barbecho',
                          date: dayjs().format('YYYY-MM-DD'),
                          children: (
                              <RegistryBarbecho
                                  onRecommendHerbicide={handleRecommendHerbicide}
                                  onRegisterApplication={() => onOpenAddModal('application')}
                              />
                          ),
                      },
                  ];

        // Once there's herbicide activity during fallow (recommended or already applied
        // products) but still no active crop monitoring, prompt for the next crop too — a
        // "Siembra" step above the Barbecho one, with no date of its own.
        const nextCropEntries: TimelineEntry[] =
            !cropMonitoring && (hasPlantingOrApplication || hasRecommendedProducts)
                ? [
                      {
                          id: 'next-crop',
                          date: dayjs().add(1, 'day').format('YYYY-MM-DD'),
                          state: { code: 'S' },
                          children: (
                              <RegistryNextCrop
                                  onRecommendHybrid={handleRecommendHybrid}
                                  onAddCrop={() => onOpenAddModal('planting')}
                              />
                          ),
                      },
                  ]
                : [];

        // Chronological, oldest first (the timeline reverses it). Entries sharing a calendar
        // day are ordered by priority before falling back to their timestamp, so a harvest
        // outranks whatever else was logged that day regardless of the hour.
        return [...otherEntries, ...barbechoEntries, ...nextCropEntries].sort((a, b) => {
            const dayDiff = dayjs(a.date).startOf('day').valueOf() - dayjs(b.date).startOf('day').valueOf();
            if (dayDiff !== 0) return dayDiff;
            const priorityDiff = (a.priority ?? 0) - (b.priority ?? 0);
            if (priorityDiff !== 0) return priorityDiff;
            return dayjs(a.date).valueOf() - dayjs(b.date).valueOf();
        });
    }, [
        showPlanting,
        showNotes,
        showApplications,
        showRecommendation,
        showVisits,
        showHarvest,
        showYieldEstimation,
        notes,
        applications,
        applicationReports,
        seedRecommendations,
        cropProtectionRecommendations,
        visitReports,
        seedRecommendedNames,
        cropProtectionRecommendedNames,
        typeNameById,
        cropMonitoring,
        fieldId,
        dateGte,
        dateLte,
        handleRecommendHerbicide,
        handleRecommendHybrid,
        onOpenAddModal,
        onOpenYieldEstimation,
    ]);

    const isLoading =
        windowLoading ||
        (showNotes && notesLoading) ||
        (showApplications && (applicationsLoading || applicationReportsLoading)) ||
        (showRecommendation && (seedRecommendationsLoading || cropProtectionRecommendationsLoading)) ||
        (showVisits && visitReportsLoading);

    const categoriesLoading =
        windowLoading ||
        notesLoading ||
        applicationsLoading ||
        applicationReportsLoading ||
        seedRecommendationsLoading ||
        cropProtectionRecommendationsLoading ||
        visitReportsLoading;

    const categoryHasData = useMemo<Record<RegistryType, boolean>>(() => {
        const standaloneNotes = notes.filter(
            (note) =>
                !note.applicationRegister &&
                !note.applicationReportResult &&
                !note.cornYieldEstimation &&
                !note.closedCropMonitoring &&
                !note.plantingDateCropMonitoring
        );
        const hasApplicationReports = applicationReports.some((report) =>
            report.results.some((result) => {
                const date = dayjs(result.eventStartDate);
                return (!dateGte || !date.isBefore(dayjs(dateGte), 'day')) && !date.isAfter(dayjs(dateLte), 'day');
            })
        );
        const daysSinceExpectedHarvest = cropMonitoring?.cornCropMonitoring?.expectedHarvestDate
            ? dayjs().diff(dayjs(cropMonitoring.cornCropMonitoring.expectedHarvestDate), 'day')
            : null;

        return {
            planting: !!cropMonitoring?.plantingDate,
            application: applications.length > 0 || hasApplicationReports,
            recommendation: seedRecommendations.length > 0 || cropProtectionRecommendations.length > 0,
            visit: visitReports.some((report) =>
                report.fieldObservations?.some((observation) => observation.field === fieldId)
            ),
            note: standaloneNotes.length > 0,
            yieldEstimation: hasYieldEstimation(cropMonitoring),
            harvest:
                !!cropMonitoring?.harvestDate ||
                (!!cropMonitoring && !cropMonitoring.harvestDate && (daysSinceExpectedHarvest ?? 0) >= 31),
        };
    }, [
        notes,
        applications,
        applicationReports,
        seedRecommendations,
        cropProtectionRecommendations,
        visitReports,
        cropMonitoring,
        fieldId,
        dateGte,
        dateLte,
    ]);

    const campaignLabel = campaign ? `${_('Campaña')} ${campaign}/${Number(campaign) + 1}` : _('Campaña');

    return (
        <Stack spacing={2} height='100%'>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent='space-between'
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={2}
            >
                <Stack direction='column' spacing={1} flex={1}>
                    <Typography variant='h5' color='primary'>
                        {_('Seguimiento de la campaña')}
                    </Typography>
                    <Typography variant='body1' color='primary.mui.inactive'>
                        {_(
                            'Registrá lo que pasa en el lote durante la campaña. Algunos datos llegan automáticamente desde FieldView.'
                        )}
                    </Typography>
                </Stack>
                <Tooltip
                    title={
                        cropMonitoring?.result?.id || cropMonitoring?.cornCropMonitoring?.phenology === 'harvested'
                            ? _('Campaña finalizada: no se pueden agregar más registros')
                            : ''
                    }
                    arrow
                >
                    <span>
                        <Button
                            variant='contained'
                            disabled={
                                !!cropMonitoring?.result?.id ||
                                cropMonitoring?.cornCropMonitoring?.phenology === 'harvested'
                            }
                            color='secondary'
                            endIcon={<Add />}
                            onClick={() => onOpenAddModal()}
                            sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'center' } }}
                        >
                            {_('Agregar registro')}
                        </Button>
                    </span>
                </Tooltip>
            </Stack>

            <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent='space-between'
                alignItems={{ xs: 'flex-start', md: 'center' }}
                spacing={2}
                sx={{ pb: 1 }}
            >
                <Typography variant='subtitle1' fontWeight='bold' color='primary' sx={{ whiteSpace: 'nowrap' }}>
                    {campaignLabel}
                </Typography>
                <Stack direction='row' spacing={1} useFlexGap flexWrap='wrap'>
                    {CATEGORIES.map((category) => (
                        <Chip
                            key={category.type}
                            label={category.label}
                            selected={selectedType === category.type}
                            disabled={!categoriesLoading && !categoryHasData[category.type]}
                            onClick={() => setSelectedType((prev) => (prev === category.type ? null : category.type))}
                        />
                    ))}
                </Stack>
            </Stack>

            {isLoading ? (
                <Skeleton
                    variant='rectangular'
                    sx={{ width: '100%', flex: 1, minHeight: { xs: 400, md: 520 }, borderRadius: 1 }}
                />
            ) : entries.length > 0 ? (
                <Timeline entries={entries} cropMonitoring={cropMonitoring} />
            ) : (
                <Stack
                    flex={1}
                    alignItems='center'
                    justifyContent='center'
                    spacing={1}
                    py={6}
                    px={4}
                    minHeight={{ xs: 300, md: 400 }}
                >
                    <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                        {_('Todavía no hay registros para mostrar.')}
                    </Typography>
                    <Typography variant='body2' color='primary.mui.inactive' textAlign='center'>
                        {_('Cuando registres actividad en esta campaña, la vas a ver acá.')}
                    </Typography>
                </Stack>
            )}
        </Stack>
    );
};

export default Registries;
